import moment from "moment";

/**
 * Format cents to USD currency string
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted currency string (e.g., "$12.34")
 */
export function formatCurrency(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);
}

/**
 * Format ISO date string to readable shift time
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted time (e.g., "Jan 5, 2:30 PM")
 */
export function formatShiftTime(isoString) {
  if (!isoString) return "";
  return moment(isoString).format("MMM D, h:mm A");
}

/**
 * Format timestamp to readable date/time
 * @param {number|string} timestamp - Unix timestamp or date string
 * @returns {string} Formatted date (e.g., "Jan 5th 2026, 2:30 pm")
 */
export function formatDateTime(timestamp) {
  if (!timestamp) return "";
  return moment(+timestamp).format("MMM Do YYYY, h:mm a");
}

/**
 * Truncate a long ID for display
 * @param {string} id - The ID to truncate
 * @param {number} maxLength - Maximum length before truncating (default: 20)
 * @returns {string} Truncated ID with ellipsis or original if short enough
 */
export function truncateId(id, maxLength = 20) {
  if (!id) return "-";
  if (id.length <= maxLength) return id;
  return `${id.substring(0, 10)}...${id.substring(id.length - 6)}`;
}

/**
 * Download data as CSV file
 * @param {string[]} headers - Column headers
 * @param {Array<Array>} rows - Data rows (array of arrays)
 * @param {string} filename - Name of the file to download
 */
export function downloadCSV(headers, rows, filename) {
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell || "").replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

/**
 * Export data array to CSV using a mapper function
 * @param {Object} options - Export options
 * @param {Array} options.data - Array of data objects to export
 * @param {Array<{header: string, value: Function|string}>} options.columns - Column definitions
 * @param {string} options.filename - Name of the file to download
 */
export function exportToCSV({ data, columns, filename }) {
  const headers = columns.map((col) => col.header);
  const rows = data.map((item) =>
    columns.map((col) =>
      typeof col.value === "function" ? col.value(item) : item[col.value]
    )
  );
  downloadCSV(headers, rows, filename);
}
